import type { Refund } from "../types/refunds"
import { mockCustomers } from "./mockCustomers"
import { mockSeedPayments } from "./mockSeedPayments"

// Helpers — derive customer and paymentId from the shared source of truth so
// all three datasets (payments, refunds, disputes) stay in sync.
const cust = (id: string) => {
  const c = mockCustomers.find((c) => c.id === id)!
  return { id: c.id, name: c.name, email: c.email }
}

const payId = (id: string) => {
  const p = mockSeedPayments.find((p) => p.id === id)
  if (!p) throw new Error(`mockRefunds references unknown seed payment: ${id}`)
  return p.id
}

export const mockRefunds: Refund[] = [
  {
    id: "re_1234567890",
    amount: 8000,
    currency: "BGN",
    decimal: 2,
    status: "succeeded",
    paymentId: payId("8a6e7a2d-0c91-4d44-bb0e-5cf4906c6a45"),
    reason: "requested_by_customer",
    created: new Date("2025-06-19T10:30:00Z"),
    customer: cust("cust_006"),
    paymentMethod: "Mastercard ••• 0036",
    description: "Refund for subscription cancellation",
    fee: 0,
    net: -8000,
    remaining: 0,
    transactionRef: "8a6e7a2d-0c91-4d44-bb0e-5cf4906c6a45",
    lastUpdated: new Date("2025-06-19T10:35:00Z"),
    processingTime: 2.1,
  },
  {
    id: "re_0987654321",
    amount: 500,
    currency: "BGN",
    decimal: 2,
    status: "pending",
    paymentId: payId("0ff165ee-6035-47e0-849c-530302075189"),
    reason: "duplicate",
    created: new Date("2025-06-20T15:45:00Z"),
    customer: cust("cust_002"),
    paymentMethod: "PayPal",
    description: "Duplicate charge refund",
    fee: 0,
    net: -500,
    transactionRef: "0ff165ee-6035-47e0-849c-530302075189",
    lastUpdated: new Date("2025-06-20T15:50:00Z"),
    processingTime: 1.8,
  },
  {
    id: "re_1122334455",
    amount: 1000,
    currency: "BGN",
    decimal: 2,
    status: "failed",
    paymentId: payId("12ba604c-703a-4cca-85c1-dc77fea3e365"),
    reason: "fraudulent",
    created: new Date("2025-06-18T10:26:27Z"),
    customer: cust("cust_005"),
    paymentMethod: "Credit Card",
    description: "Fraudulent transaction refund attempt",
    fee: 0,
    net: 0,
    transactionRef: "12ba604c-703a-4cca-85c1-dc77fea3e365",
    lastUpdated: new Date("2025-06-18T10:28:00Z"),
    processingTime: 0.5,
  },
  {
    id: "re_5566778899",
    amount: 5000,
    currency: "BGN",
    decimal: 2,
    status: "succeeded",
    paymentId: payId("23ba9cc9-3350-46cb-88e6-7027f669342c"),
    reason: "requested_by_customer",
    created: new Date("2025-06-18T10:26:27Z"),
    customer: cust("cust_010"),
    paymentMethod: "Credit Card",
    description: "Product return refund",
    fee: 0,
    net: -5000,
    remaining: 0,
    transactionRef: "23ba9cc9-3350-46cb-88e6-7027f669342c",
    lastUpdated: new Date("2025-06-18T10:28:00Z"),
    processingTime: 3.2,
  },
  {
    id: "re_9988776655",
    amount: 1000,
    currency: "BGN",
    decimal: 2,
    status: "canceled",
    paymentId: payId("0541af14-f40c-4c63-8120-3de24687501f"),
    reason: "requested_by_customer",
    created: new Date("2025-06-18T10:26:27Z"),
    customer: cust("cust_004"),
    paymentMethod: "Credit Card",
    description: "Canceled refund request",
    fee: 0,
    net: 0,
    transactionRef: "0541af14-f40c-4c63-8120-3de24687501f",
    lastUpdated: new Date("2025-06-18T10:28:00Z"),
    processingTime: 0.3,
  },
  {
    id: "re_partial_001",
    amount: 5000,
    currency: "BGN",
    decimal: 2,
    status: "partially_refunded",
    paymentId: payId("3d45e9ac-9c9f-4208-ab08-2bb6228ada27"),
    reason: "requested_by_customer",
    created: new Date("2025-06-18T10:26:27Z"),
    customer: cust("cust_014"),
    paymentMethod: "Credit Card",
    description: "Partial refund for damaged product",
    fee: 0,
    net: -5000,
    remaining: 5000,
    transactionRef: "3d45e9ac-9c9f-4208-ab08-2bb6228ada27",
    lastUpdated: new Date("2025-06-18T10:28:00Z"),
    processingTime: 2.7,
  },
  {
    id: "re_requested_001",
    amount: 3500,
    currency: "BGN",
    decimal: 2,
    status: "refund_requested",
    paymentId: payId("a1b2c3d4-e5f6-7890-abcd-ef1234567890"),
    reason: "product_unacceptable",
    created: new Date("2025-06-21T09:00:00Z"),
    customer: cust("cust_008"),
    paymentMethod: "Visa ••• 4242",
    description: "Refund request awaiting approval — product did not match description",
    fee: 0,
    net: -3500,
    transactionRef: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    lastUpdated: new Date("2025-06-21T09:05:00Z"),
    processingTime: 0,
  },
  {
    id: "re_enterprise_001",
    amount: 1200,
    currency: "BGN",
    decimal: 2,
    status: "succeeded",
    paymentId: payId("ed600cf8-3d7e-477f-9986-0afe0c909a33"),
    reason: "requested_by_customer",
    created: new Date("2025-06-18T10:26:28Z"),
    customer: cust("cust_003"),
    paymentMethod: "Bank Transfer",
    description: "Enterprise license refund",
    fee: 0,
    net: -1200,
    remaining: 0,
    transactionRef: "ed600cf8-3d7e-477f-9986-0afe0c909a33",
    lastUpdated: new Date("2025-06-18T10:28:00Z"),
    processingTime: 5.8,
  },
  {
    id: "re_international_001",
    amount: 12541,
    currency: "BGN",
    decimal: 2,
    status: "succeeded",
    paymentId: payId("237ce88c-758e-4048-9ace-756486929296"),
    reason: "requested_by_customer",
    created: new Date("2025-06-18T10:26:28Z"),
    customer: cust("cust_013"),
    paymentMethod: "Credit Card",
    description: "Annual subscription refund",
    fee: 0,
    net: -12541,
    remaining: 0,
    transactionRef: "237ce88c-758e-4048-9ace-756486929296",
    lastUpdated: new Date("2025-06-18T10:28:00Z"),
    processingTime: 4.1,
  },
  {
    id: "re_mobile_001",
    amount: 5000,
    currency: "BGN",
    decimal: 2,
    status: "pending",
    paymentId: payId("df83920d-38b9-457e-8034-7b5e0e92dd3a"),
    reason: "requested_by_customer",
    created: new Date("2025-06-18T10:26:29Z"),
    customer: cust("cust_007"),
    paymentMethod: "Google Pay",
    description: "Mobile app subscription refund",
    fee: 0,
    net: -5000,
    transactionRef: "df83920d-38b9-457e-8034-7b5e0e92dd3a",
    lastUpdated: new Date("2025-06-18T10:28:00Z"),
    processingTime: 1.2,
  },
  {
    id: "re_student_001",
    amount: 2000,
    currency: "BGN",
    decimal: 2,
    status: "succeeded",
    paymentId: payId("6f2aa264-b240-4e87-8ac5-f72ee400d643"),
    reason: "requested_by_customer",
    created: new Date("2025-06-18T10:26:31Z"),
    customer: cust("cust_001"),
    paymentMethod: "Credit Card",
    description: "Student subscription refund",
    fee: 0,
    net: -2000,
    remaining: 0,
    transactionRef: "6f2aa264-b240-4e87-8ac5-f72ee400d643",
    lastUpdated: new Date("2025-06-18T10:28:00Z"),
    processingTime: 0.8,
  },
]

export const refundReasons = [
  "requested_by_customer",
  "duplicate",
  "fraudulent",
  "subscription_canceled",
  "product_unacceptable",
  "product_not_received",
  "unrecognized",
  "credit_not_processed",
  "general",
]
